import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import userActivityLogService, { UserActivityLog, ActivityLogFilter } from '../services/userActivityLogService.ts';

interface UserActivityLogsProps {
  className?: string;
}

const UserActivityLogs: React.FC<UserActivityLogsProps> = ({ className = '' }) => {
  const [logs, setLogs] = useState<UserActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<ActivityLogFilter>({
    limit: 50
  });
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // 활동 로그 불러오기
  const loadActivityLogs = async () => {
    setLoading(true);
    try {
      const activityLogs = await userActivityLogService.getAllActivityLogs(filter);
      setLogs(activityLogs);
    } catch (error) {
      console.error('활동 로그 불러오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActivityLogs();
  }, [filter]);

  // 필터 변경 핸들러
  const handleFilterChange = (newFilter: Partial<ActivityLogFilter>) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
  };

  // 필터 초기화
  const resetFilter = () => {
    setFilter({ limit: 50 });
  };

  // 로그 상세 토글
  const toggleLogDetails = (logId: string) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  // 액션 타입별 색상 클래스
  const getActionColorClass = (action: string): string => {
    const colorMap: { [key: string]: string } = {
      'LOGIN': 'bg-green-100 text-green-800',
      'LOGOUT': 'bg-gray-100 text-gray-800',
      'PAGE_VISIT': 'bg-blue-100 text-blue-800',
      'DATA_CREATE': 'bg-purple-100 text-purple-800',
      'DATA_UPDATE': 'bg-yellow-100 text-yellow-800',
      'DATA_DELETE': 'bg-red-100 text-red-800',
      'FILE_UPLOAD': 'bg-indigo-100 text-indigo-800',
      'EXPORT_DATA': 'bg-pink-100 text-pink-800',
      'USER_CREATE': 'bg-emerald-100 text-emerald-800',
      'USER_UPDATE': 'bg-amber-100 text-amber-800',
      'USER_DELETE': 'bg-rose-100 text-rose-800',
      'TEAM_CREATE': 'bg-cyan-100 text-cyan-800',
      'TEAM_UPDATE': 'bg-teal-100 text-teal-800',
      'TEAM_DELETE': 'bg-orange-100 text-orange-800',
      'PAYROLL_CALCULATE': 'bg-violet-100 text-violet-800',
      'PERFORMANCE_REFRESH': 'bg-lime-100 text-lime-800'
    };
    return colorMap[action] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          📊 사용자 활동 로그
        </h3>
        <button
          onClick={loadActivityLogs}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? '로딩중...' : '새로고침'}
        </button>
      </div>

      {/* 필터 섹션 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">필터</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              이메일 주소
            </label>
            <input
              type="text"
              value={filter.userEmail || ''}
              onChange={(e) => handleFilterChange({ userEmail: e.target.value || undefined })}
              placeholder="이메일 주소 입력"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              사용자 이름
            </label>
            <input
              type="text"
              value={filter.userName || ''}
              onChange={(e) => handleFilterChange({ userName: e.target.value || undefined })}
              placeholder="사용자 이름 입력"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              액션 타입
            </label>
            <select
              value={filter.action || ''}
              onChange={(e) => handleFilterChange({ action: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">전체</option>
              <option value="LOGIN">로그인</option>
              <option value="LOGOUT">로그아웃</option>
              <option value="PAGE_VISIT">페이지 방문</option>
              <option value="DATA_CREATE">데이터 생성</option>
              <option value="DATA_UPDATE">데이터 수정</option>
              <option value="DATA_DELETE">데이터 삭제</option>
              <option value="FILE_UPLOAD">파일 업로드</option>
              <option value="USER_CREATE">사용자 생성</option>
              <option value="TEAM_CREATE">팀 생성</option>
              <option value="PAYROLL_CALCULATE">급여 계산</option>
              <option value="PERFORMANCE_REFRESH">성과 데이터 새로고침</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              표시 개수
            </label>
            <select
              value={filter.limit || 50}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={25}>25개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={resetFilter}
            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            필터 초기화
          </button>
          <div className="text-xs text-gray-500 flex items-center">
            💡 이메일이나 사용자 이름의 일부만 입력해도 검색됩니다
          </div>
        </div>
      </div>

      {/* 로그 테이블 */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">활동 로그를 불러오는 중...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          활동 로그가 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시간
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  액션
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상세 내용
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  페이지
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  세션
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleLogDetails(log.id!)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {format(log.timestamp, 'MM/dd HH:mm:ss', { locale: ko })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div>
                        <div className="font-medium text-gray-900">
                          {log.userName || log.userEmail}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {log.userEmail}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColorClass(log.action)}`}>
                        {userActivityLogService.getActionDisplayName(log.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                      {log.details}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {log.page || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        {log.sessionId?.slice(-8) || '-'}
                      </span>
                    </td>
                  </tr>
                  {expandedLogId === log.id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 bg-gray-50">
                        <div className="text-sm">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-gray-700">전체 시간:</span>
                              <span className="ml-2 text-gray-900">
                                {format(log.timestamp, 'yyyy년 MM월 dd일 HH:mm:ss', { locale: ko })}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">사용자 ID:</span>
                              <span className="ml-2 text-gray-900 font-mono">{log.userId}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">세션 ID:</span>
                              <span className="ml-2 text-gray-900 font-mono">{log.sessionId || '-'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">IP 주소:</span>
                              <span className="ml-2 text-gray-900">{log.ipAddress || '-'}</span>
                            </div>
                          </div>
                          {log.userAgent && (
                            <div className="mt-2">
                              <span className="font-medium text-gray-700">사용자 에이전트:</span>
                              <div className="mt-1 text-gray-900 text-xs break-all bg-white p-2 rounded border">
                                {log.userAgent}
                              </div>
                            </div>
                          )}
                          {log.details && (
                            <div className="mt-2">
                              <span className="font-medium text-gray-700">상세 내용:</span>
                              <div className="mt-1 text-gray-900 bg-white p-2 rounded border">
                                {log.details}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 로그 개수 표시 */}
      {!loading && logs.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          총 {logs.length}개의 활동 로그가 표시되었습니다.
        </div>
      )}
    </div>
  );
};

export default UserActivityLogs; 